import { Chip } from "@mui/material";
import { useState } from "react";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import { formatDate, formatTime } from "../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../utils/utils";

const PatientPortalPastVisit = () => {
  const dummyData = [
    {
      uuid: "1",
      dateTime: "2023-09-21T15:05:00",
      providerName: "Dr. Corina",
      reasonForVisit: "Chronic back pain worsening",
      appointmentType: "New Appointment",
      appointmentStatus: "Completed",
      billStatus: "Paid",
      hasSummary: true,
    },
    {
      uuid: "2",
      dateTime: "2023-08-18T16:12:00",
      providerName: "Dr. Miller",
      reasonForVisit: "Sudden unexplained weight loss",
      appointmentType: "Follow Up",
      appointmentStatus: "Rescheduled",
      billStatus: null,
      hasSummary: false,
    },
    {
      uuid: "3",
      dateTime: "2023-06-04T00:14:00",
      providerName: "Dr. Corina",
      reasonForVisit: "Severe recurring headaches",
      appointmentType: "Follow Up",
      appointmentStatus: "Canceled",
      billStatus: null,
      hasSummary: false,
    },
    {
      uuid: "4",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Lilly Turner",
      reasonForVisit: "Persistent joint pain and swelling",
      appointmentType: "New Appointment",
      appointmentStatus: "Rescheduled",
      billStatus: null,
      hasSummary: false,
    },
    {
      uuid: "5",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Miller",
      reasonForVisit: "Severe recurring headaches",
      appointmentType: "New Appointment",
      appointmentStatus: "Completed",
      billStatus: "Paid",
      hasSummary: true,
    },
    {
      uuid: "6",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Miller",
      reasonForVisit: "Unexplained fatigue and weakness",
      appointmentType: "Follow Up",
      appointmentStatus: "Canceled",
      billStatus: null,
      hasSummary: false,
    },
    {
      uuid: "7",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Lilly Turner",
      reasonForVisit: "Chronic skin rash spreading",
      appointmentType: "Follow Up",
      appointmentStatus: "Completed",
      billStatus: "Due",
      hasSummary: true,
    },
    {
      uuid: "8",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Lilly Turner",
      reasonForVisit: "Follow up consultation",
      appointmentType: "Follow Up",
      appointmentStatus: "Rescheduled",
      billStatus: null,
      hasSummary: false,
    },
    {
      uuid: "9",
      dateTime: "2023-05-13T08:05:00",
      providerName: "Dr. Corina",
      reasonForVisit: "Routine checkup",
      appointmentType: "Follow Up",
      appointmentStatus: "Rescheduled",
      billStatus: null,
      hasSummary: false,
    },
  ];

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return { color: "#049B22", bgColor: "#E1FCDE" };
      case "rescheduled":
        return { color: "#943C00", bgColor: "#FFF2D2" };
      case "cancelled":
        return { color: "#B1000F", bgColor: "#FFF2F3" };
      default:
        return { color: "#000000", bgColor: "#F5F5F5" };
    }
  };

  const getBillStatusColor = (status: string | null) => {
    if (!status) return null;
    switch (status?.toLowerCase()) {
      case "paid":
        return { color: "#049B22", bgColor: "#E1FCDE" };
      case "due":
        return { color: "#943C00", bgColor: "#FFF2D2" };
      default:
        return { color: "#000000", bgColor: "#F5F5F5" };
    }
  };

  const formatDateTime = (dateTime: string) => {
    if (!dateTime) return "-";
    const date = formatDate(dateTime);
    const time = formatTime(dateTime);
    return `${date} at ${time}`;
  };

  const columns = applyDefaultColumnSettings<any>([
    {
      field: "dateTime",
      headerName: "Date & Time",
      flex: 1.2,
      renderCell: ({ value }) => <Text>{formatDateTime(value as string)}</Text>,
    },
    {
      field: "providerName",
      headerName: "Name",
      flex: 1,
      renderCell: ({ value }) => (
        <Flex flexDirection="column" gap={0.3}>
          <Text>{value}</Text>
          <Text fontSize="12px" color="text.secondary">
            Primary provider
          </Text>
        </Flex>
      ),
    },
    {
      field: "reasonForVisit",
      headerName: "Reason for Visit",
      flex: 1.2,
    },
    {
      field: "appointmentType",
      headerName: "Appointment Type",
      flex: 1,
    },
    {
      field: "appointmentStatus",
      headerName: "Appointment Status",
      flex: 1,
      renderCell: ({ value }) => {
        const statusColor = getStatusColor(value as string);
        return (
          <Chip
            size="small"
            label={value}
            sx={{
              color: statusColor.color,
              bgcolor: statusColor.bgColor,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "billStatus",
      headerName: "Bill Status",
      flex: 1,
      renderCell: ({ value }) => {
        if (!value) return <Text>-</Text>;
        const statusColor = getBillStatusColor(value as string | null);
        if (!statusColor) return <Text>-</Text>;
        return (
          <Chip
            size="small"
            label={value}
            sx={{
              color: statusColor.color,
              bgcolor: statusColor.bgColor,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      renderCell: ({ row }) => {
        if (!row.hasSummary) return <Text>-</Text>;
        return (
          <Text
            color="primary.main"
            sx={{ cursor: "pointer", color: "#104E84", fontWeight: 500 }}
            onClick={() => {
              // TODO: Implement view summary functionality
            }}
          >
            View Summary
          </Text>
        );
      },
    },
  ]);

  return (
    <CustomDataGridV2
      getRowId={(row) => row?.uuid}
      rows={dummyData ?? []}
      rowCount={dummyData?.length ?? 10}
      columns={columns}
      paginationModel={pagination}
      onPaginationModelChange={setPagination}
    />
  );
};

export default PatientPortalPastVisit;
